import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCommon } from '../context/CommonContext';
import api from '../api/Api';
import Loading from './Loading';
import { useLanguage } from '../context/LanguageProvider';

const PanelistNumpad = () => {

    console.log("------------------panelist rendered----------------------");

    // Destructuring needed values and setters from CommonContext
    // surveyId — the current survey identifier  
    // userId — the panelist's input ID  
    // setUserId — setter for updating panelist input value  
    // setMemories — setter for storing 'memories' returned from API  
    // inputUserIDText — mutable ref to a display string for label
    const { surveyId, userId, setUserId, setMemories, inputUserIDText } = useCommon();

    // translation — function to translate keys into localized strings
    const { translation } = useLanguage();

    // Initialize the navigation hook
    const navigate = useNavigate();

    // Local state for error feedback to the user
    const [errorMessage, setErrorMessage] = useState('');
    // Local state to disable user input while checking credentials to prevent multiple requests
    const [isInputDisabled, setIsInputDisabled] = useState(false);

    /**
     * Handles all button clicks from the numpad (digits, backspace "←", and "enter").
     * @param {string} value — The value of the clicked button
     */
    const handleButtonClick = (value) => {
        const currentId = userId || ''; // fallback to empty string if null

        // If input is locked, ignore all presses
        if (isInputDisabled) return; 

        // Append digit to ID if it's less than max length and not special keys
        if (currentId.length < 10) {
            if (value !== '←' && value !== 'enter') {
                setUserId(currentId + value);
                setErrorMessage(""); // clear any previous error
            }
        }

        // Remove last character on backspace
        if (value === '←') {
            setUserId(currentId.slice(0, -1));
            setErrorMessage("");
        }

        // Handle "enter" press
        if (value === 'enter') {
            // If no ID entered, show prompt from translations
            if (userId == null || userId == "") {
                setErrorMessage(translation("Panelist_panelistIDPrompt"));
            } else {
                // Lock input to prevent multiple submissions
                setIsInputDisabled(true);
                // Trigger login check with entered ID
                checkLogIn();
            }
        }
    };

    /**
     * Async API request to check if entered user ID matches an existing roster entry
     * If valid, stores 'memories' and navigates to Welcome page
     * If invalid, shows error and re-enables input
     */
    const checkLogIn = async () => {
        try {

            // // ignoring user ID 100 for user exisitance check
            // if(userId != 100){

            //     const { exists } = await api.userExists(surveyId, userId);
    
            //     if (exists) {
            //         // User already registered in this survey — stop
            //         setErrorMessage(translation("Panelist_idExistsError")); 
            //         setIsInputDisabled(false);
            //         return;
            //     }
            // }
        
            // Request to server: fetch roster entry for this survey ID and panelist ID
            const data = await api.fetchRoster(surveyId, userId);
            // if user already exist in the results then show error
            if (data.exists) {
                setErrorMessage(translation("panelist_idExistsError"));
                setIsInputDisabled(false);
                return;
            }

            // If valid response and user ID matches input
            if (data && data.user_id == userId) {
                // Convert server CSV into array
                const memoriesArray = data.memories.split(',');
                // Store in context for later use
                setMemories(memoriesArray);
                // Redirect user to Welcome screen
                navigate('/Welcome');
            }

        } catch (error) {
            // On error: unlock input and show "invalid ID" message
            setIsInputDisabled(false);
            setErrorMessage(translation("invalidIDMessage"));
        }
        console.log("Checking login for:", userId);
    };

    /**
     * Updates ID field when user types directly into input
     * Only accepts numeric values due to /^\d*$/ regex
     */
    const handleInputChange = (e) => {
        const value = e.target.value;
        if (/^\d*$/.test(value) && !isInputDisabled) {
            setUserId(value);
            setErrorMessage("");
        }
    };

    /**
     * Handles pressing Enter key inside the input box.
     * Calls button click logic for 'enter'.
     */
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleButtonClick("enter");
        }
    };

    // While checking login (input disabled), show loading spinner instead of UI
    if (isInputDisabled) {
        return <Loading />;
    }

    return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
            <div className='flex flex-col items-center justify-center relative'>

                {/* Heading & label for User ID input */}
                <div className="mb-4">
                    <h1 className="text-center font-bold text-4xl mb-4">
                        <label htmlFor='userIDlogin'>
                            {inputUserIDText.current}
                        </label>
                    </h1>  
                </div>

                {/* Container for Input + Numpad */}
                <div className="bg-black p-8 rounded-md">
                    
                    {/* Input area with thick border — matches touchscreen style */}
                    <div className="border-t-8 border-l-8 border-r-8 border-b-4 border-black">
                        <input
                            type="text"
                            id="userIDlogin"
                            value={userId || ''}              // fallback for null/undefined
                            onChange={handleInputChange}      // controlled component handler
                            onKeyDown={handleKeyDown}         // submit on Enter press
                            disabled={isInputDisabled}        // lock while loading
                            className="w-96 h-20 p-2 border-4 border-gray-600 rounded-lg 
                                       outline-black text-center text-5xl font-sans 
                                       font-medium text-black"
                        />
                    </div>

                    {/* Numpad grid: digits, backspace '←', and 'enter' */}
                    <div className="grid grid-cols-3 gap-4 mt-5 justify-items-center">
                        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '←', '0', 'enter'].map((value, index) => (
                            <div
                                key={index}
                                onClick={() => handleButtonClick(value)}
                                className="flex items-center justify-center w-16 h-16 border-3 
                                           border-white bg-gray-200 rounded-md cursor-pointer shadow-lg"
                            >
                                {/* Show correct label for special keys */}
                                <span className="text-2xl font-medium">
                                    {value}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Error message shown below UI when validation fails */}
                {errorMessage && (
                    <span className="absolute -bottom-10 text-red-600 text-2xl font-bold">
                        {errorMessage}
                    </span>
                )}
            </div>
        </div>
    );
};

export default PanelistNumpad;
